import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { useAuthStore } from '@/shared/store/authStore'
import { rememberLastOrg } from '@/features/auth/lastOrg'
import type { LoginResponsePayload } from '@/features/auth/types'

export interface Studio {
  slug: string
  name: string
  roles: string[]
}

export interface MyStudiosResponse {
  /** Slug of the studio this session is scoped to — null for an identity-only session. */
  current: string | null
  studios: Studio[]
}

/**
 * The studios the signed-in identity can enter.
 *
 * Reachable by an identity-only session (SecurityConfig lists /auth/my-studios
 * on the identity-only surface), which is what lets the zero-membership page
 * be nothing more than this query's empty case.
 */
export function useMyStudios(enabled = true) {
  return useQuery<MyStudiosResponse>({
    queryKey: queryKeys.auth.myStudios,
    queryFn: async () => {
      const { data } = await apiClient.get('/auth/my-studios')
      return data
    },
    enabled,
    // Membership sets change when someone is invited or removed — minutes-old
    // is fine, and the switcher opens far more often than that changes.
    // `refetch()` (the zero-membership page's "Check again") ignores this.
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Swap the current session for one scoped to another studio, without re-login.
 *
 * The server resolves the caller's OWN memberships and rejects anything outside
 * that set — naming a slug here proves nothing, so there is no client-side
 * check worth doing beyond rendering only what my-studios returned.
 *
 * `setAuth` wipes the TanStack cache whenever the authenticated identity/org
 * actually changes (see authStore), so the previous studio's cached clients,
 * finance and settings cannot be served inside the new one.
 *
 * **The navigation lives here, not at the call sites.** Both of them (the user-menu
 * switcher and the zero-membership page's picker) want the same destination, and one
 * of them cannot own it: `StudioSwitcher` closes the menu around itself the instant a
 * switch starts, and TanStack Query drops `mutate(vars, { onSuccess })` callbacks when
 * the calling component unmounts before the mutation settles. So the switcher's own
 * `onSuccess` never ran and an in-app studio switch left the person on the OLD
 * studio's URL, inside the new studio — the org changed underneath the page they were
 * already looking at. A mutation-level callback like this one always runs.
 *
 * Destination is the index route, deliberately:
 *  - never the URL we were on — every id in it (/projects/42, /clients/7) belongs to
 *    the studio being LEFT, so following it is a 404 at best;
 *  - never a hard-coded /dashboard either — `HomeRedirect` is mounted at `/` and is
 *    the only reader of the member's `home_page` preference for the studio being
 *    ENTERED, falling back to ROLE_DEFAULTS (F12, same root cause as F1/F10 on the
 *    sign-in path). `setAuth` has already wiped the cache for the org change, so that
 *    preference is re-read for the new studio rather than served from the old one's.
 */
export function useSwitchStudio() {
  const setAuth = useAuthStore(state => state.setAuth)
  const navigate = useNavigate()

  return useMutation<LoginResponsePayload, unknown, string>({
    mutationFn: async (orgSlug: string) => {
      const { data } = await apiClient.post('/auth/switch-studio', { orgSlug })
      return data
    },
    onSuccess: data => {
      if (!data.accessToken || !data.member) return
      rememberLastOrg(data.member.orgSlug)
      setAuth(data.accessToken, data.member)
      navigate('/')
    },
  })
}
